import {useState} from 'react';
import get from 'lodash/get';
import moment from 'moment';

import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import {DateField} from '@mui/x-date-pickers/DateField';
import Radio from "@mui/material/Radio";
import Stack from '@mui/material/Stack';
import Typography from "@mui/material/Typography";

import * as formValidations from '../../helpers/validations';
import CustomInput from '../common/CustomInput';
import CustomTextField from '../common/CustomTextField';
import PatientConsentAgreement from './PatientConsentAgreement';
import PrivacyAgreement from './PrivacyAgreement';
import TitleDivider from "./TitleDivider";

import './ConsentForms.scss';

const defaultFormValues = {
  dateOfBirth: '',
  contractName: 'MovementX Consent & HIPAA Privacy Policy',
  signature: '',
};

const validations = {
  signature: formValidations.name,
  dateOfBirth: str => str.length,
};

const handleFieldUpdate = (formValues, setFormValues) => fieldName => e => {
  const value = (
    e.format
    ? e.format('MM/DD/yyyy')
    : get(e, 'target.value', '')
      .replace(/^\s/g, '')
      .replace(/\s$/g, '')
  );
  const values = {
    ...formValues,
    [fieldName]: value,
  };
  return setFormValues(values);
};

const checkIfFormIsValid = (
  formValues,
  validationDict,
  hasReadPatientAgreement,
  hasReadHipaaAgreement
) => {
  if (!hasReadPatientAgreement) return false;
  if (!hasReadHipaaAgreement) return false;
  const containsInvalidValues = Object.keys(validationDict).reduce((acc, k) => (
    validationDict[k] ? acc : false
  ), true);
  return containsInvalidValues;
};

const ConsentForms = props => {
  const {onSubmit,user} = props;
  const priorValues = (
    user && user.date_birth
    ? {
      dateOfBirth: moment(user.date_birth).format('MM/DD/YYYY'),
      signature: user.contract_signature,
      contractName: defaultFormValues.contractName,
      sms_consent: user.sms_consent,
    }
    : null
  );
  const [smsConsent, setSMSConsent] = useState(!!priorValues?.sms_consent);
  const [formValues, setFormValues] = useState(priorValues || defaultFormValues);
  const [hasReadPatientAgreement, setHasReadPatientAgreement] = useState(false);
  const [hasReadHipaaAgreement, setHasReadHipaaAgreement] = useState(false);
  const onChangeHandler = handleFieldUpdate(formValues, setFormValues);
  const validationDict = Object.keys(validations).reduce((acc, k) => ({
    ...acc,
    [k]: validations[k](formValues[k])
  }), {});
  const formIsValid = checkIfFormIsValid(
    formValues,
    validationDict,
    hasReadPatientAgreement,
    hasReadHipaaAgreement
  );
  return (
    <Stack className="ConsentForms" gap={1} paddingBottom='36px'>
      <Typography
        variant='large500TightInter'
        display='block'
      >
        We value your trust, safety, and privacy. Please read the following consent forms carefully.
      </Typography>

      <TitleDivider text='MovementX Patient Agreement'/>

      <Box sx={{marginTop: '3px'}}>
        <PatientConsentAgreement/>
      </Box>

      <Stack direction="row">
        <a
          download
          href="/pdfs/MovementX-PatientConsentAgreement.pdf"
          className="consent-form__download-link"
        >
          <Button
            size='small'
            variant="primaryOutline"
            className="ConsentForms__download-button"
          >
            <div className="ConsentForms__download-button-icon"/>
            <Typography variant='normal' color='primary' letterSpacing='0px'>
              Download
            </Typography>
          </Button>
        </a>
      </Stack>

      <FormGroup>
      <FormControlLabel
          id="legal-checkbox"
          sx={{padding: '0 7.5px', height: '38px'}}
          control={
            <Radio
              size='small'
              onChange={e => setHasReadPatientAgreement(e.target.checked)}
            />
          }
          label={(
            <Typography variant='large500TightInter'>
              I have read and agree to the MovementX Patient Agreement
              <strong className='ConsentForms__agreement-radio__required'>{' '}*</strong>
            </Typography>
          )}
        />
      </FormGroup>

      <Box marginTop={0}></Box>

      <Box marginBottom='3px'>
        <TitleDivider text='HIPAA Privacy Policy' />
      </Box>

      <PrivacyAgreement/>

      <Stack direction="row" spacing={2}>
        <a
          download
          href="/pdfs/MovementX-PrivacyPolicy.pdf"
          className="ConsentForms__download-link"
        >
          <Button
            size='small'
            className="ConsentForms__download-button"
            variant='primaryOutline'
          >
            <i className="ConsentForms__download-button-icon"/>
            <Typography variant='normal' color='primary'>
              Download
            </Typography>
          </Button>
        </a>
      </Stack>

      <FormGroup>
        <FormControlLabel
          sx={{ marginRight: 0, paddingLeft: '7.5px', height: '24px'}}
          id="legal-checkbox"
          control={
            <Radio
              size='small'
              onChange={e => setHasReadHipaaAgreement(e.target.checked)}
            />
          }
          label={
            <Typography variant='large500TightInter' className="ConsentForms__agreement-radio">
              I have read and agree to the HIPAA Privacy Policy
              <strong className='ConsentForms__agreement-radio__required'>{' '}*</strong>
            </Typography>
          }
        />
      </FormGroup>

      {!!props?.includeSMSConsent && <Box mb={2}>
        <TitleDivider text='Text & Call Reminders' />
      </Box>}

      {!!props?.includeSMSConsent && <FormGroup>
        <FormControlLabel
          sx={{ marginRight: 0, paddingLeft: '7.5px', height: '24px', marginBottom: '12px'}}
          id="sms-consent-checkbox"
          control={
            <Radio
              size='small'
              onChange={e => setSMSConsent(e.target.checked)}
            />
          }
          label={
            <Typography variant='large500TightInter' className="ConsentForms__agreement-radio">
              Text or call me with helpful booking reminders and care updates at the number I provided. 
              Reply STOP anytime. See 
              {' '}<a href='https://movement-x.com/sms-terms/' className='link' target='_blank'>SMS Terms</a> 
              {' '}and 
              {' '}<a href='https://movement-x.com/privacy-policy/' className='link' target='_blank'>Privacy Policy</a>.
              <strong className='ConsentForms__agreement-radio__required'>{' '}*</strong>
            </Typography>
          }
        />
      </FormGroup>}
      <CustomInput
        id="dateOfBirth"
        className="ConsentForms__date-of-birth"
        label="Patient Date of Birth"
        variant="outlined"
        required
      >
        <DateField
          required
          defaultValue={
            user.date_birth
              ? moment(user.date_birth)
              : null
          }
          onChange={onChangeHandler('dateOfBirth')}
          sx={{bgcolor: '#ffffff'}}
        />
      </CustomInput>

      <CustomTextField
        id="signature"
        className="ConsentForms__signature"
        label="Electronic Signature"
        placeholder="Jane Smith"
        variant="outlined"
        required
        defaultValue={get(user, 'contract_signature')}
        onChange={onChangeHandler('signature')}
        helperText={'I have read and agree with the above consent forms. Furthermore, I understand that all electronic signatures are\n' +
          '        the legal equivalent of my handwritten signature.'}
      />

      <Button
        sx={{alignSelf: 'center'}}
        variant="primaryGradient"
        size="medium"
        fullWidth={false}
        disabled={!formIsValid}
        onClick={() => onSubmit({...formValues, sms_consent: smsConsent})}
      >
        Continue to Payment
      </Button>
    </Stack>
  );
};

export default ConsentForms;
